// AI Tips Configuration for different pages in the application

export const aiTipsConfig = {
  '/dash': [
    {
      title: 'Welcome to Pool Route Monitor!',
      description: 'This is your central dashboard where you can see an overview of your business. From here, you can quickly navigate to manage customers, employees, and view important metrics.',
      category: 'Getting Started',
      actionItems: [
        'View active and inactive customer counts',
        'Navigate to customer or employee management',
        'Access quick action tiles for common tasks'
      ]
    },
    {
      title: 'Quick Navigation Tiles',
      description: 'The tiles on your dashboard provide quick access to the most commonly used features. Click on "Active Customers" to view and edit customer information, or "Team Members" to handle your workforce.',
      category: 'Navigation',
      actionItems: [
        'Click tiles to navigate to different sections',
        'View real-time counts and metrics',
        'Access detailed management pages'
      ]
    },
    {
      title: 'Using the Sidebar Menu',
      description: 'Click the hamburger menu icon in the top-left to access all features. You can schedule work, view routes, generate reports, and manage your subscription from the sidebar.',
      category: 'Navigation',
      actionItems: [
        'Click the menu icon to open the sidebar',
        'Explore Employee Scheduling, Route Summary, Reports, Manage Subscriptions, Recovery Templates, and Help & FAQ',
        'Access your profile from the top-right avatar'
      ]
    }
  ],

  '/view-customer': [
    {
      title: 'Customer Management Overview',
      description: 'This page displays all your customers in an organized table. You can search, filter, edit, and manage customer information efficiently.',
      category: 'Customers',
      actionItems: [
        'Search for specific customers using the search bar',
        'Click on a customer row to view details',
        'Use action buttons to edit or delete customers'
      ]
    },
    {
      title: 'Adding New Customers',
      description: 'Click the "Add Customer" button to create a new customer record. Fill in all required information including name, address, contact details, and service preferences.',
      category: 'Customers',
      actionItems: [
        'Click "Add Customer" button',
        'Fill in customer details accurately',
        'Save to add them to your active customer list'
      ]
    },
    {
      title: 'Customer Status Management',
      description: 'Toggle between active and inactive customers using the status filter. This helps you manage your current customer base and keep track of past clients.',
      category: 'Customers',
      actionItems: [
        'Filter by active or inactive status',
        'Update customer status as needed',
        'Export customer lists for reporting'
      ]
    }
  ],

  '/add-customer': [
    {
      title: 'Creating a New Customer',
      description: 'Fill in all the required fields to add a new customer. Make sure to provide accurate contact information and service address for proper route planning.',
      category: 'Customers',
      actionItems: [
        'Enter customer name and contact details',
        'Provide accurate service address',
        'Specify service type and frequency',
        'Save to create the customer record'
      ]
    },
    {
      title: 'Important Customer Information',
      description: 'Include email and phone number for better communication. The service address is crucial for route optimization and scheduling.',
      category: 'Customers',
      actionItems: [
        'Double-check contact information',
        'Verify service address accuracy',
        'Add any special instructions or notes'
      ]
    }
  ],

  '/view-emp': [
    {
      title: 'Employee Management',
      description: 'View and manage all your employees from this page. You can see their roles, contact information, and assign them to routes and tasks.',
      category: 'Employees',
      actionItems: [
        'View employee details in the table',
        'Edit employee information as needed',
        'Assign employees to work orders and routes'
      ]
    },
    {
      title: 'Adding New Employees',
      description: 'Click "Add Employee" to register new team members. Provide their contact information, designation, and any relevant certifications.',
      category: 'Employees',
      actionItems: [
        'Click "Add Employee" button',
        'Enter employee details',
        'Assign appropriate role and permissions'
      ]
    }
  ],

  '/add-emp': [
    {
      title: 'Registering a New Employee',
      description: 'Fill in the employee information form carefully. Include their name, contact details, designation, and any certifications they hold.',
      category: 'Employees',
      actionItems: [
        'Enter employee name and contact info',
        'Select their job designation',
        'Add certifications if applicable',
        'Save to add them to your team'
      ]
    }
  ],

  '/employee-scheduling': [
    {
      title: 'Employee Scheduling Overview',
      description: 'Plan and manage your team\'s work schedules. Assign employees to different routes and time slots to optimize your service delivery.',
      category: 'Scheduling',
      actionItems: [
        'View weekly/monthly schedule calendar',
        'Drag and drop to assign employees',
        'Check for schedule conflicts',
        'Save your scheduling changes'
      ]
    },
    {
      title: 'Optimizing Your Schedule',
      description: 'Balance workload across your team and ensure all routes are covered. Consider employee skills and customer preferences when scheduling.',
      category: 'Scheduling',
      actionItems: [
        'Distribute work evenly across employees',
        'Match skilled employees to specific tasks',
        'Avoid double-booking employees',
        'Leave buffer time for travel'
      ]
    }
  ],

  '/routes-option-1': [
    {
      title: 'Route Management',
      description: 'View and organize your service routes. Optimize routes to reduce travel time and increase efficiency. Each route shows assigned customers and employees.',
      category: 'Routes',
      actionItems: [
        'View all active routes',
        'Edit route details and assignments',
        'Optimize route order for efficiency',
        'Assign employees to specific routes'
      ]
    },
    {
      title: 'Route Optimization Tips',
      description: 'Group customers by geographic proximity to minimize travel time. Consider service requirements and employee expertise when building routes.',
      category: 'Routes',
      actionItems: [
        'Group nearby customers together',
        'Consider traffic patterns and time windows',
        'Balance route workload',
        'Update routes as customer base changes'
      ]
    }
  ],

  '/work-orders': [
    {
      title: 'Work Order Management',
      description: 'Track all your service work orders from this page. Create new work orders, assign them to employees, and monitor their completion status.',
      category: 'Work Orders',
      actionItems: [
        'View all pending and completed work orders',
        'Create new work orders for customers',
        'Assign work orders to employees',
        'Track completion status and notes'
      ]
    },
    {
      title: 'Creating Work Orders',
      description: 'Click "Create Work Order" to generate a new service task. Select the customer, service type, date, and assign an employee.',
      category: 'Work Orders',
      actionItems: [
        'Click "Create Work Order" button',
        'Select customer and service type',
        'Choose date and time',
        'Assign to available employee'
      ]
    }
  ],

  '/reports': [
    {
      title: 'Business Reports',
      description: 'Generate and view various business reports including service completion rates, revenue analysis, and employee performance metrics.',
      category: 'Reports',
      actionItems: [
        'Select report type from dropdown',
        'Choose date range for analysis',
        'Generate and view reports',
        'Export reports to PDF or Excel'
      ]
    },
    {
      title: 'Using Reports for Insights',
      description: 'Use reports to identify trends, optimize operations, and make data-driven decisions. Regular reporting helps track business growth and identify areas for improvement.',
      category: 'Reports',
      actionItems: [
        'Review reports weekly/monthly',
        'Compare performance over time',
        'Identify top-performing routes/employees',
        'Use insights to improve operations'
      ]
    }
  ],

  '/billing': [
    {
      title: 'Subscription Management',
      description: 'Manage your Pool Route Monitor subscription here. View your current plan, payment history, and update payment methods.',
      category: 'Billing',
      actionItems: [
        'View current subscription plan',
        'Check payment history',
        'Update payment methods',
        'View billing invoices'
      ]
    }
  ],

  '/map-view': [
    {
      title: 'Map View Overview',
      description: 'Visualize your customers and routes on an interactive map. This helps you understand geographic distribution and plan efficient routes.',
      category: 'Maps',
      actionItems: [
        'View all customer locations on the map',
        'Click markers to see customer details',
        'Visualize route paths',
        'Identify service coverage areas'
      ]
    },
    {
      title: 'Using Map for Route Planning',
      description: 'Use the map to identify clusters of customers and optimize your route planning. Zoom in and out to get different perspectives of your service area.',
      category: 'Maps',
      actionItems: [
        'Zoom to see details or overview',
        'Click customer markers for information',
        'Identify geographic patterns',
        'Plan routes based on proximity'
      ]
    }
  ],

  '/my-account': [
    {
      title: 'Account Settings',
      description: 'Manage your personal account information, change password, and update your profile details from this page.',
      category: 'Account',
      actionItems: [
        'Update your personal information',
        'Change your password',
        'Configure notification preferences',
        'View your account activity'
      ]
    }
  ]
};

// Function to get tips for a specific page
export const getTipsForPage = (pathname) => {
  return aiTipsConfig[pathname] || [];
};

// Function to check if a page has tips available
export const hasTipsForPage = (pathname) => {
  return aiTipsConfig.hasOwnProperty(pathname) && aiTipsConfig[pathname].length > 0;
};
